<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input
          v-model="startDate"
          type="date"
          class="form-control"
        />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input
          v-model="endDate"
          type="date"
          class="form-control"
        />
      </div>
      <div class="filter-group">
        <button class="btn btn-primary" @click="fetchUsageStats">
          <i class="ri-search-line"></i>
          查詢
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchUsageStats">重試</button>
    </div>

    <div v-else class="usage-content">
      <!-- 統計摘要 -->
      <div class="stats-summary">
        <div class="stat-card">
          <div class="stat-label">總成本</div>
          <div class="stat-value">{{ formatTwdFromStored(totalCost) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">總請求數</div>
          <div class="stat-value">{{ totalRequests }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">總輸入 Token</div>
          <div class="stat-value">{{ totalInputTokens.toLocaleString() }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">總輸出 Token</div>
          <div class="stat-value">{{ totalOutputTokens.toLocaleString() }}</div>
        </div>
      </div>

      <!-- 使用量列表 -->
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>日期</th>
              <th>成本 (台幣)</th>
              <th>請求數</th>
              <th>輸入 Token</th>
              <th>輸出 Token</th>
              <th>平均信心度</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="usageStats.length === 0">
              <td colspan="6" class="empty-state">
                沒有找到使用量記錄
              </td>
            </tr>
            <tr v-for="stat in usageStats" :key="stat.stat_date">
              <td>{{ formatDate(stat.stat_date) }}</td>
              <td>{{ formatTwdFromStored(stat.total_cost_twd || 0) }}</td>
              <td>{{ stat.total_requests || 0 }}</td>
              <td>{{ (stat.total_input_tokens || 0).toLocaleString() }}</td>
              <td>{{ (stat.total_output_tokens || 0).toLocaleString() }}</td>
              <td>
                <span v-if="stat.avg_confidence" class="badge badge-info">
                  {{ (parseFloat(stat.avg_confidence) * 100).toFixed(0) }}%
                </span>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError } from '@/utils/swal.js';
import { formatTwdFromStored } from '@/utils/currency.js';

export default {
  name: 'ChatMateUsage',
  setup() {
    const { pageTitle } = usePageTitle('使用量統計');
    const usageStats = ref([]);
    const loading = ref(false);
    const error = ref(null);
    
    // 預設查詢最近 30 天
    const endDate = ref(new Date().toISOString().split('T')[0]);
    const startDate = ref(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);

    const totalCost = computed(() => {
      return usageStats.value.reduce((sum, stat) => {
        return sum + parseFloat(stat.total_cost_twd || 0);
      }, 0);
    });

    const totalRequests = computed(() => {
      return usageStats.value.reduce((sum, stat) => {
        return sum + (stat.total_requests || 0);
      }, 0);
    });

    const totalInputTokens = computed(() => {
      return usageStats.value.reduce((sum, stat) => {
        return sum + (parseInt(stat.total_input_tokens || 0));
      }, 0);
    });

    const totalOutputTokens = computed(() => {
      return usageStats.value.reduce((sum, stat) => {
        return sum + (parseInt(stat.total_output_tokens || 0));
      }, 0);
    });

    const fetchUsageStats = async () => {
      if (!startDate.value || !endDate.value) {
        await showError('查詢失敗', '請選擇開始日期和結束日期');
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        const response = await chatmateService.getUsageStats({
          startDate: startDate.value,
          endDate: endDate.value
        });

        if (response && response.success && response.data) {
          usageStats.value = response.data || [];
        } else {
          error.value = response?.message || '獲取使用量統計失敗';
        }
      } catch (err) {
        console.error('獲取使用量統計失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取使用量統計失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    // 不在 onMounted 時自動查詢，等用戶點擊查詢按鈕
    // onMounted(() => {
    //   fetchUsageStats();
    // });

    return {
      pageTitle,
      usageStats,
      loading,
      error,
      startDate,
      endDate,
      totalCost,
      totalRequests,
      totalInputTokens,
      totalOutputTokens,
      fetchUsageStats,
      formatDate,
      formatTwdFromStored
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.usage-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.stat-card {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  font-weight: 600;
  color: #333;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}
</style>
